import { Badge, Banner, Button, Dialog, Field, Input, Select, Switch } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { palette } from '../lib/palette';
import { DangerButton } from './ui/DangerButton';
import { ICONS } from './ui/icons';
import { DangerConfirmModal } from './DangerConfirmModal';
import { SkeletonText } from './ui/Skeleton';

interface BackupFile {
  fileName: string;
  size: number;
  createdAt: string;
  status: 'success' | 'failed' | 'in_progress';
  type: 'full' | 'database' | 'uploads';
}

interface BackupConfig {
  enabled: boolean;
  schedule: string;
  retentionDays: number;
  storageType: 'local' | 's3';
}

const SCHEDULE_OPTIONS = [
  { value: '0 */6 * * *', label: 'A cada 6 horas' },
  { value: '0 */12 * * *', label: 'A cada 12 horas' },
  { value: '0 3 * * *', label: 'Diariamente às 3h' },
  { value: '0 3 * * 0', label: 'Semanalmente (domingo às 3h)' },
  { value: '0 3 1 * *', label: 'Mensalmente (dia 1 às 3h)' }
];
// Horários no fuso America/Sao_Paulo (o agendador do backend usa esse fuso)

export function SystemBackup() {
  const toast = useAppToast();
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [config, setConfig] = useState<BackupConfig>({
    enabled: false,
    schedule: '0 3 * * *',
    retentionDays: 14,
    storageType: 'local'
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupFile | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadBackups();
    loadConfig();
  }, []);

  const authenticatedFetch = useAuthenticatedFetch();

  const loadBackups = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system');
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      } else {
        throw new Error('Erro ao carregar backups');
      }
    } catch (error) {
      console.error('Erro ao carregar backups:', error);
      toast.error('Erro ao carregar lista de backups');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/config');
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setConfig(data.config);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await authenticatedFetch('/api/backup/system', {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Backup criado com sucesso!');
        await loadBackups();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao criar backup');
      }
    } catch (error: any) {
      console.error('Erro ao criar backup:', error);
      toast.error(error.message || 'Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/configure', {
        method: 'POST',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuração salva com sucesso!');
        setShowConfigModal(false);
        await loadConfig();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao salvar configuração');
      }
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.message || 'Erro ao salvar configuração');
    }
  };

  // Restaurar substitui o banco inteiro: a confirmação exige digitar RESTAURAR
  const [confirmRestore, setConfirmRestore] = useState<'selected' | 'upload' | null>(null);

  const handleRestoreBackup = () => {
    if (!selectedBackup) return;
    setShowRestoreModal(false); // a confirmação digitada abre sozinha, sem empilhar janelas
    setConfirmRestore('selected');
  };

  const executeRestoreBackup = async () => {
    if (!selectedBackup) return;

    setRestoring(true);
    try {
      const response = await authenticatedFetch('/api/backup/system/restore', {
        method: 'POST',
        body: JSON.stringify({ fileName: selectedBackup.fileName })
      });

      if (response.ok) {
        toast.success('Backup restaurado com sucesso! Recomenda-se reiniciar o sistema.');
        setShowRestoreModal(false);
        setSelectedBackup(null);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao restaurar backup');
      }
    } catch (error: any) {
      console.error('Erro ao restaurar backup:', error);
      toast.error(error.message || 'Erro ao restaurar backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadBackup = async (fileName: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/backup/system/download/${fileName}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer download do backup');
      }

      // Criar blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Download iniciado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao fazer download:', error);
      toast.error(error.message || 'Erro ao fazer download do backup');
    }
  };

  const handleUploadAndRestore = async () => {
    if (!uploadFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }

    // Validar extensão do arquivo
    if (!uploadFile.name.endsWith('.tar.gz')) {
      toast.error('Arquivo deve ser um backup válido (.tar.gz)');
      return;
    }

    setShowUploadModal(false);
    setConfirmRestore('upload');
  };

  const executeUploadAndRestore = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('backup', uploadFile);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/backup/system/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Backup enviado com sucesso! Aguarde a restauração...');

        // Agora restaurar o backup que acabou de fazer upload
        const restoreResponse = await authenticatedFetch('/api/backup/system/restore', {
          method: 'POST',
          body: JSON.stringify({ fileName: data.fileName })
        });

        if (restoreResponse.ok) {
          toast.success('Sistema restaurado com sucesso! Recomenda-se reiniciar os serviços.');
          setShowUploadModal(false);
          setUploadFile(null);
          await loadBackups();
        } else {
          const error = await restoreResponse.json();
          throw new Error(error.message || 'Erro ao restaurar backup');
        }
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao enviar arquivo de backup');
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload e restaurar:', error);
      toast.error(error.message || 'Erro ao fazer upload e restaurar backup');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SkeletonText lines={3} className="w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header e Ações */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h4 className="text-base font-semibold">Backup do Sistema</h4>
            <p className="text-sm text-gray-600 mt-1">
              Gerencie backups completos do sistema (banco de dados + arquivos)
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" icon={ICONS.Settings} onClick={() => setShowConfigModal(true)}>
              Configurar
            </Button>
            <DangerButton
              variant="dangerOutline"
              icon={ICONS.Import}
              
              onClick={() => setShowUploadModal(true)}
            >
              Restaurar de arquivo
            </DangerButton>
            <Button variant="primary" icon={ICONS.Plus} onClick={handleCreateBackup} loading={creating}>
              Criar backup
            </Button>
          </div>
        </div>

        {/* Status do Backup Automático */}
        <div className={`px-4 py-3 rounded-lg ${config.enabled ? 'bg-green-50' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${config.enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Backup automático: {config.enabled ? 'ativo' : 'desativado'}
                </p>
                {config.enabled && (
                  <p className="text-xs text-gray-600 mt-1">
                    Agendamento: {SCHEDULE_OPTIONS.find(opt => opt.value === config.schedule)?.label || config.schedule} · guarda {config.retentionDays} dias
                  </p>
                )}
              </div>
            </div>
            <Button variant="secondary" onClick={() => setShowConfigModal(true)}>
              {config.enabled ? 'Alterar' : 'Ativar'}
            </Button>
          </div>
        </div>
      </div>

      {/* Lista de Backups */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Backups Disponíveis</h3>
          <p className="text-sm text-gray-600 mt-1">{backups.length} {backups.length === 1 ? 'backup' : 'backups'} · ficam no próprio servidor</p>
        </div>

        {backups.length === 0 ? (
          <div className="p-12 text-center">
            <ICONS.Database size={24} />
            <p className="mt-4 text-sm text-gray-500">Nenhum backup encontrado</p>
            <p className="text-xs text-gray-400 mt-1">Clique em "Criar backup" para gerar o primeiro</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {backups.map((backup) => (
              <div key={backup.fileName} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div
                        aria-hidden
                        style={{
                          width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                          background: palette.blue.light3, color: palette.blue.base,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <ICONS.Database size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{backup.fileName}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-gray-500">{formatFileSize(backup.size)}</span>
                          <span className="text-xs text-gray-500">{formatDate(backup.createdAt)}</span>
                          <Badge variant={backup.status === 'success' ? 'green' : backup.status === 'failed' ? 'red' : 'orange'}>
                            {backup.status === 'success' ? 'Completo' : backup.status === 'failed' ? 'Falhou' : 'Em andamento'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="secondary" icon={ICONS.Download} onClick={() => handleDownloadBackup(backup.fileName)}>
                      Baixar
                    </Button>
                    <DangerButton
                      variant="dangerOutline"
                      onClick={() => {
                        setSelectedBackup(backup);
                        setShowRestoreModal(true);
                      }}
                    >
                      Restaurar
                    </DangerButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Configurar backup automático */}
      <Dialog.Root open={showConfigModal} onOpenChange={(o: boolean) => { if (!o) (() => setShowConfigModal(false))(); }}>
<Dialog size="sm" className="p-6">


        <h3 className="text-lg font-semibold" style={{ marginBottom: 4 }}>Backup automático</h3>
        <div style={{ color: palette.gray.dark1, marginBottom: 20 }}>
          Backup completo do sistema (banco de dados e arquivos), no horário de Brasília.
        </div>
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div className="font-semibold" id="backup-enabled-label">Ativar backup automático</div>
            <Switch onCheckedChange={(checked: boolean) => setConfig({ ...config, enabled: checked })} aria-label="Ativar backup automático" checked={config.enabled} />
          </div>
          {config.enabled && (
            <>
              <Field label="Frequência">
                <Select value={(SCHEDULE_OPTIONS.find((option) => option.value === config.schedule) ?? null)?.value ?? null} items={SCHEDULE_OPTIONS} onValueChange={(value: string | null) => setConfig({ ...config, schedule: value ?? config.schedule })} />
              </Field>
              <Field label="Guardar backups por (dias)" description="Backups mais antigos são apagados do servidor automaticamente">
                <Input onChange={(e) => setConfig({ ...config, retentionDays: parseInt(e.target.value) || 1 })} type="number" min={1} max={365} value={String(config.retentionDays)} />
              </Field>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveConfig}>Salvar</Button>
        </div>
      
</Dialog>
</Dialog.Root>

      {/* Restaurar um backup da lista */}
      <Dialog.Root open={showRestoreModal && !!selectedBackup} onOpenChange={(o: boolean) => { if (!o) (() => { setShowRestoreModal(false); setSelectedBackup(null); })(); }}>
<Dialog size="sm" className="p-6">


        <h3 className="text-lg font-semibold" style={{ marginBottom: 12 }}>Restaurar backup</h3>
        <div style={{ marginBottom: 16 }}>
          <Banner variant="error" description={<>
            Todo o sistema volta ao estado deste backup. O banco de dados e os arquivos atuais são substituídos, de todas as empresas.
          </>} />
        </div>
        {selectedBackup && (
          <div className="backup-file-summary">
            <ICONS.Database size={16} />
            <div style={{ minWidth: 0 }}>
              <div className="font-semibold" style={{ wordBreak: 'break-all' }}>{selectedBackup.fileName}</div>
              <div className="text-kumo-subtle text-xs">{formatDate(selectedBackup.createdAt)} · {formatFileSize(selectedBackup.size)}</div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" onClick={() => { setShowRestoreModal(false); setSelectedBackup(null); }}>Cancelar</Button>
          <DangerButton onClick={handleRestoreBackup} loading={restoring}>Continuar</DangerButton>
        </div>
      
</Dialog>
</Dialog.Root>

      {/* Restaurar a partir de um arquivo */}
      <Dialog.Root open={showUploadModal} onOpenChange={(o: boolean) => { if (!o) (() => { setShowUploadModal(false); setUploadFile(null); })(); }}>
<Dialog size="sm" className="p-6">


        <h3 className="text-lg font-semibold" style={{ marginBottom: 4 }}>Restaurar de arquivo</h3>
        <div style={{ color: palette.gray.dark1, marginBottom: 16 }}>
          Para levar o sistema para um servidor novo, migrar de outro ambiente ou recuperar a partir de uma cópia
          externa (por exemplo, a do Cloudflare R2).
        </div>
        <div style={{ marginBottom: 16 }}>
          <Banner variant="error" description={<>
            O arquivo enviado substitui todo o banco de dados e todos os arquivos atuais do sistema.
          </>} />
        </div>

        <input
          id="backupFile"
          type="file"
          accept=".tar.gz"
          className="sr-only"
          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
        />
        {uploadFile ? (
          <div className="backup-file-summary">
            <ICONS.File size={16} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="font-semibold" style={{ wordBreak: 'break-all' }}>{uploadFile.name}</div>
              <div className="text-kumo-subtle text-xs">{formatFileSize(uploadFile.size)}</div>
            </div>
            <Button variant="secondary" onClick={() => document.getElementById('backupFile')?.click()}>Trocar</Button>
          </div>
        ) : (
          <Button variant="secondary" icon={ICONS.Upload} onClick={() => document.getElementById('backupFile')?.click()}>
            Escolher arquivo .tar.gz
          </Button>
        )}

        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" onClick={() => { setShowUploadModal(false); setUploadFile(null); }}>Cancelar</Button>
          <DangerButton onClick={handleUploadAndRestore} disabled={!uploadFile} loading={uploading}>
            Continuar
          </DangerButton>
        </div>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal
        open={confirmRestore !== null}
        title="Restaurar o sistema inteiro?"
        confirmText="Restaurar"
        requiredText="RESTAURAR"
        onCancel={() => setConfirmRestore(null)}
        onConfirm={() => {
          const which = confirmRestore;
          setConfirmRestore(null);
          if (which === 'selected') executeRestoreBackup();
          else if (which === 'upload') executeUploadAndRestore();
        }}
      >
        {confirmRestore === 'upload' ? 'O arquivo enviado' : `O backup ${selectedBackup?.fileName ?? ''}`} substitui
        todo o banco de dados e os arquivos atuais, de todas as empresas. Tudo o que foi criado depois dele é
        perdido e todas as conexões ativas caem. Não dá para desfazer.
      </DangerConfirmModal>
    </div>
  );
}
